import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { LogoMark, Wordmark } from "@/components/Logo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const { user, setUser } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setStatus("error"); setError("Missing verification link."); return; }
    api.verifyEmail(token)
      .then(() => {
        setStatus("ok");
        // If this browser is already logged in as the account that just verified, reflect it
        // immediately instead of waiting for the next /auth/me refresh.
        if (user) setUser({ ...user, email_verified: true });
      })
      .catch((e) => { setStatus("error"); setError(apiError(e, "Verification failed")); });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-sm text-center fade-up">
        <div className="flex items-center justify-center gap-3 mb-8">
          <LogoMark size={36} />
          <Wordmark className="text-lg text-white" />
        </div>
        {status === "loading" && (
          <>
            <CircleNotch size={32} className="animate-spin mx-auto mb-3" color="var(--accent)" />
            <p className="text-sm" style={{ color: "var(--text-2)" }}>Verifying your email...</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle size={40} weight="fill" color="var(--success)" className="mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Email verified</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>Your account is confirmed — you're all set.</p>
            <button onClick={() => nav(user ? "/" : "/login")} className="px-6 py-2.5 text-sm font-bold text-white hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
              {user ? "Go to dashboard" : "Sign in"}
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={40} weight="fill" color="var(--danger)" className="mx-auto mb-3" />
            <h2 className="text-xl font-bold mb-2">Couldn't verify</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>{error}</p>
            <p className="text-sm" style={{ color: "var(--text-2)" }}>
              <Link to="/settings" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>Resend a new link from Settings</Link>, or <Link to="/login" className="font-semibold hover:underline" style={{ color: "var(--accent)" }}>sign in</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
